import { Component, OnDestroy, OnInit } from '@angular/core';
import { DocumentViewerTraceService } from '../../services/document-viewer-trace.service';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';
import { DwComponent, DwComponentType, DwRoutingService } from '@devwareapps/devware-cap';
import { DocumentsManagerService } from '../../services/documents-manager.service';
import { DocumentFolderEntity, DocumentGroupAllItems, DocumentGroupEntity } from '../../../../meta-data/app-meta-data.service';
import { DocumentCardData, DocumentCardType } from '../../models/document-card-data.model';
import { DocumentSheetService } from '../../services/document-sheet.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';


@DwComponent({
  componentType: DwComponentType.pageComponent,
  key: 'documents-page',
  name: 'Documents Page',
  isGlobal: true
})
@Component({
  selector: 'app-documents-page',
  templateUrl: './documents-page.component.html',
  styleUrls: ['./documents-page.component.scss']
})
export class DocumentsPageComponent implements OnInit, OnDestroy {
  private readonly traceId: number;
  documentGroupId: number;
  documentFolderId?: number;
  documentsGroup: DocumentGroupEntity;
  documentCards: DocumentCardData[] = [];
  allDocumentCards: DocumentCardData[] = [];
  documentFolder: DocumentFolderEntity;

  selectedCardUniqueKey: string;
  viewingDocument: boolean;
  documentItemId: number;

  canEditFolder: boolean = false;

  loading = false;
  isProtools = false;
  readonly skeletonCards = [0, 1, 2, 3, 4, 5];

  constructor(
    private documentRepositoryService: DocumentsRepositoryService,
    private documentManagerService: DocumentsManagerService,
    private dwRoutingService: DwRoutingService,
    private documentSheetService: DocumentSheetService,
    private router: Router,
    private readonly viewerTrace: DocumentViewerTraceService

  ) {
    this.traceId = viewerTrace.nextViewerId();
  }

  ngOnInit(): void {
    this.viewerTrace.record('documents-page.created', { pageId: this.traceId });
    this.setupDocuments();
    this.setupEvents();
  }

  ngOnDestroy(): void {
    this.viewerTrace.record('documents-page.destroyed', { pageId: this.traceId });
  }

  setupDocuments() {
    this.viewerTrace.record('documents-page.load-start', { pageId: this.traceId }, true);
    this.documentGroupId = this.documentManagerService.determineDocumentGroup();
    this.isProtools = this.documentGroupId === DocumentGroupAllItems.ProTools;

    this.loading = true;
    this.documentRepositoryService.getDocumentGroup(this.documentGroupId).subscribe(documentsGroup => {
      this.viewerTrace.record('documents-page.group-emitted', { pageId: this.traceId });
      this.documentsGroup = documentsGroup;

      this.allDocumentCards = this.documentManagerService.buildAllDocumentCards(this.documentsGroup);

      this.setupCurrentFolder();

      this.canEditFolder = this.documentManagerService.canEditFolder(documentsGroup, this.documentFolder);

      this.loading = false;
    });

    this.documentSheetService.documentDataUpdated$.subscribe((dataUpdate) => {
      this.viewerTrace.record('documents-page.data-updated', { pageId: this.traceId });
      this.allDocumentCards = this.documentManagerService.handleDocumentDataUpdate(dataUpdate, this.documentsGroup);
      this.setupCurrentDocumentCards();
    });


  }

  setupEvents() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event) => {
      this.setupCurrentFolder();
    });
  }

  setupCurrentFolder() {
    this.viewerTrace.record('documents-page.route-applied', { pageId: this.traceId }, true);
    this.documentFolder = this.documentManagerService.determineCurrentFolder(this.allDocumentCards);
    this.documentFolderId = this.documentFolder?.DocumentFolderId;
    this.canEditFolder = this.documentManagerService.canEditFolder(this.documentsGroup, this.documentFolder);

    this.viewingDocument = this.documentManagerService.isViewingDocument();

    if (this.viewingDocument) {
      this.documentItemId = this.documentManagerService.getDocumentItemIdFromUrl();
    }

    this.setupCurrentDocumentCards();
  }

  cardClick(card: DocumentCardData) {
    this.documentManagerService.navigateToDocument(card, this.allDocumentCards);
  }

  cardConfigureClick(card: DocumentCardData) {

    this.selectedCardUniqueKey = card.uniqueKey;
    this.documentSheetService.openSheet(card);
  }

  addDocument() {
    const newCardData: DocumentCardData = {
      documentCardType: DocumentCardType.documentItem,
      uniqueKey: '-1',
      newItem: true,
      parentFolderId: this.documentFolderId,
      groupOrder: -1
    };

    this.documentSheetService.openSheet(newCardData);
  }

  
  addFolder() {
    const newCardData: DocumentCardData = {
      documentCardType: DocumentCardType.folder,
      uniqueKey: '-1',
      newItem: true,
      parentFolderId: this.documentFolderId,
      groupOrder: -1
    };

    this.documentSheetService.openSheet(newCardData);
  }

  setupCurrentDocumentCards() {
    this.documentCards = this.documentManagerService.getCurrentDocumentCards(this.allDocumentCards, this.documentFolderId);
  }

  // Context-aware empty state messaging
  get emptyStateTitle(): string {
    return this.documentFolderId 
      ? 'This folder is empty' 
      : 'No documents yet';
  }

  get emptyStateMessage(): string {
    return this.documentFolderId
      ? 'Add documents or create subfolders to organize your content.'
      : 'Get started by creating your first folder or uploading a document.';
  }

}
